import React, { useEffect, useState } from 'react'
import { useTaskStore } from '@store'
import { useAuth } from '@context/AuthContext'
import taskService from '@services/taskService'
import { TaskActionsModal } from '@components/features/tasks'
import './MyTasks.scss'

/**
 * My Tasks Page - Modern Redesign
 *
 * Features:
 * - Polished statistics cards with visual hierarchy
 * - Action-focused "Needs Your Attention" section for pending approvals
 * - Clear actionable items with contextual buttons
 * - Modern, clean design with smooth interactions
 */
const MyTasks = () => {
	const { user: authUser } = useAuth()
	const {
		myTasks,
		myApprovals,
		statistics,
		loading,
		pagination,
		approvalsPagination,
		fetchMyTasks,
		fetchMyApprovals,
		fetchStatistics,
		setPage,
		setApprovalsPage,
	} = useTaskStore()

	const [activeTab, setActiveTab] = useState('my-tasks')
	const [searchTerm, setSearchTerm] = useState('')
	const [statusFilter, setStatusFilter] = useState('all')
	const [projectFilter, setProjectFilter] = useState(null)

	// Modal state
	const [modalState, setModalState] = useState({
		isOpen: false,
		action: null,
		task: null,
	})

	// FALLBACK: If user is null in AuthContext, get from storage directly
	let user = authUser
	if (!user) {
		try {
			const userDataStr = sessionStorage.getItem('auth_user_data') || localStorage.getItem('auth_user_data')
			if (userDataStr) {
				user = JSON.parse(userDataStr)
			}
		} catch (error) {
			console.error('Error parsing user data from storage:', error)
		}
	}

	const userRoleSlug = user?.role?.slug || user?.role_slug

	// Fetch data on mount and when filters change
	useEffect(() => {
		fetchStatistics()
		// Also fetch approvals to get the count of tasks needing YOUR approval
		fetchMyApprovals({ page: 1, limit: 1 }) // Just need the count
	}, [fetchStatistics, fetchMyApprovals])

	useEffect(() => {
		if (activeTab === 'my-tasks') {
			fetchMyTasks({
				search: searchTerm,
				workflow_status: statusFilter !== 'all' ? statusFilter : undefined,
				project_id: projectFilter,
			})
		} else if (activeTab === 'my-approvals') {
			fetchMyApprovals({
				search: searchTerm,
				project_id: projectFilter,
			})
		}
	}, [
		activeTab,
		searchTerm,
		statusFilter,
		projectFilter,
		fetchMyTasks,
		fetchMyApprovals,
	])

	// Get current task list and pagination based on active tab
	const getCurrentTaskList = () => {
		return activeTab === 'my-tasks' ? myTasks : myApprovals
	}

	const getCurrentPagination = () => {
		return activeTab === 'my-tasks' ? pagination : approvalsPagination
	}

	// Handle page change
	const handlePageChange = (newPage) => {
		if (activeTab === 'my-tasks') {
			setPage(newPage)
			// Fetch tasks with new page
			fetchMyTasks({
				search: searchTerm,
				workflow_status: statusFilter !== 'all' ? statusFilter : undefined,
				project_id: projectFilter,
				page: newPage,
			})
		} else {
			setApprovalsPage(newPage)
			// Fetch approvals with new page
			fetchMyApprovals({
				search: searchTerm,
				project_id: projectFilter,
				page: newPage,
			})
		}
	}

	// Modal handlers
	const openModal = (action, task) => {
		setModalState({ isOpen: true, action, task })
	}

	const closeModal = () => {
		setModalState({ isOpen: false, action: null, task: null })
	}

	// Calculate actionable tasks count
	const actionableCount = (statistics.not_started || 0) + (statistics.in_progress || 0) + (statistics.rejected || 0)
	// Tasks YOU submitted that are awaiting review from others
	const awaitingReviewCount = statistics.pending_review || 0
	// Tasks that need YOUR approval (from My Approvals API)
	const pendingApprovalCount = approvalsPagination.total || 0

	const currentTaskList = getCurrentTaskList()
	const currentPagination = getCurrentPagination()

	return (
		<div className="my-tasks-page">
			{/* Page Header */}
			<div className="page-header">
				<div className="page-header__content">
					<h1 className="page-title">My Tasks</h1>
					<p className="page-subtitle">Manage your assigned tasks and pending approvals</p>
				</div>
			</div>

			{/* Attention Banner - Shows when there are pending approvals */}
			{pendingApprovalCount > 0 && activeTab === 'my-tasks' && (
				<div className="attention-banner">
					<div className="attention-banner__icon">
						<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
							<path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
							<line x1="12" y1="9" x2="12" y2="13"/>
							<line x1="12" y1="17" x2="12.01" y2="17"/>
						</svg>
					</div>
					<div className="attention-banner__content">
						<span className="attention-banner__text">
							<strong>{pendingApprovalCount} task{pendingApprovalCount > 1 ? 's' : ''}</strong> awaiting your review
						</span>
						<button
							className="attention-banner__action"
							onClick={() => setActiveTab('my-approvals')}
						>
							Review Now
							<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
								<polyline points="9 18 15 12 9 6"/>
							</svg>
						</button>
					</div>
				</div>
			)}

			{/* Statistics Cards */}
			<div className="stats-grid">
				<div
					className="stat-card stat-card--actionable"
					onClick={() => {
						setActiveTab('my-tasks')
						setStatusFilter('all')
					}}
				>
					<div className="stat-card__content">
						<div className="stat-card__icon">
							<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
								<path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
							</svg>
						</div>
						<div className="stat-card__info">
							<span className="stat-card__title">Tasks to Work On</span>
							<span className="stat-card__value">{actionableCount}</span>
						</div>
					</div>
				</div>

				<div
					className="stat-card stat-card--approvals"
					onClick={() => setActiveTab('my-approvals')}
				>
					<div className="stat-card__content">
						<div className="stat-card__icon">
							<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
								<path d="M9 11l3 3L22 4"/>
								<path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
							</svg>
						</div>
						<div className="stat-card__info">
							<span className="stat-card__title">Pending Approvals</span>
							<span className="stat-card__value">{pendingApprovalCount}</span>
						</div>
					</div>
				</div>

				<div
					className="stat-card stat-card--awaiting"
					onClick={() => {
						setActiveTab('my-tasks')
						setStatusFilter('pending_review')
					}}
				>
					<div className="stat-card__content">
						<div className="stat-card__icon">
							<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
								<circle cx="12" cy="12" r="10"/>
								<polyline points="12 6 12 12 16 14"/>
							</svg>
						</div>
						<div className="stat-card__info">
							<span className="stat-card__title">Awaiting Review</span>
							<span className="stat-card__value">{awaitingReviewCount}</span>
						</div>
					</div>
				</div>

				<div className="stat-card stat-card--completed">
					<div className="stat-card__content">
						<div className="stat-card__icon">
							<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
								<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
								<polyline points="22 4 12 14.01 9 11.01"/>
							</svg>
						</div>
						<div className="stat-card__info">
							<span className="stat-card__title">Completed</span>
							<span className="stat-card__value">{statistics.completed || 0}</span>
						</div>
					</div>
				</div>
			</div>

			{/* Tab Navigation */}
			<div className="task-section">
				<div className="task-section__header">
					<div className="task-tabs">
						<button
							className={`task-tab ${activeTab === 'my-tasks' ? 'task-tab--active' : ''}`}
							onClick={() => setActiveTab('my-tasks')}
						>
							<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
								<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
								<polyline points="14 2 14 8 20 8"/>
								<line x1="16" y1="13" x2="8" y2="13"/>
								<line x1="16" y1="17" x2="8" y2="17"/>
							</svg>
							My Tasks
							{actionableCount > 0 && (
								<span className="task-tab__badge">{actionableCount}</span>
							)}
						</button>

						<button
							className={`task-tab ${activeTab === 'my-approvals' ? 'task-tab--active' : ''} ${pendingApprovalCount > 0 ? 'task-tab--attention' : ''}`}
							onClick={() => setActiveTab('my-approvals')}
						>
							<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
								<path d="M9 11l3 3L22 4"/>
								<path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
							</svg>
							My Approvals
							{pendingApprovalCount > 0 && (
								<span className="task-tab__badge task-tab__badge--urgent">{pendingApprovalCount}</span>
							)}
						</button>
					</div>

					<div className="task-filters">
						<div className="search-wrapper">
							<svg className="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
								<circle cx="11" cy="11" r="8"/>
								<line x1="21" y1="21" x2="16.65" y2="16.65"/>
							</svg>
							<input
								type="text"
								placeholder="Search tasks..."
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								className="search-input"
							/>
						</div>

						{activeTab === 'my-tasks' && (
							<select
								value={statusFilter}
								onChange={(e) => setStatusFilter(e.target.value)}
								className="status-select"
							>
								<option value="all">All Status</option>
								<option value="not_started">Not Started</option>
								<option value="pending_assignment">Pending Assignment</option>
								<option value="in_progress">In Progress</option>
								<option value="pending_review">Pending Review</option>
								<option value="approved">Completed</option>
								<option value="rejected">Rejected</option>
							</select>
						)}
					</div>
				</div>

				{/* Task List */}
				<div className="task-list-container">
					{loading ? (
						<div className="loading-state">
							<div className="spinner"></div>
							<p>Loading tasks...</p>
						</div>
					) : currentTaskList.length === 0 ? (
						<div className="empty-state">
							<div className="empty-state__icon">
								{activeTab === 'my-tasks' ? (
									<svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
										<path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/>
										<rect x="9" y="3" width="6" height="4" rx="2"/>
										<path d="M9 14l2 2 4-4"/>
									</svg>
								) : (
									<svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
										<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
										<polyline points="22 4 12 14.01 9 11.01"/>
									</svg>
								)}
							</div>
							<h3 className="empty-state__title">
								{activeTab === 'my-tasks' ? 'All caught up!' : 'No pending approvals'}
							</h3>
							<p className="empty-state__message">
								{activeTab === 'my-tasks'
									? 'You have no tasks assigned to you at the moment.'
									: 'You have no tasks pending your approval.'}
							</p>
						</div>
					) : (
						<>
							<div className="task-list">
								{currentTaskList.map((task) => (
									<TaskCard
										key={task.id}
										task={task}
										user={user}
										userRoleSlug={userRoleSlug}
										isApprovalView={activeTab === 'my-approvals'}
										onAction={openModal}
									/>
								))}
							</div>

							{/* Pagination */}
							{currentPagination.pages > 1 && (
								<div className="pagination">
									<button
										className="pagination__btn"
										disabled={currentPagination.page === 1}
										onClick={() => handlePageChange(currentPagination.page - 1)}
									>
										<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
											<polyline points="15 18 9 12 15 6"/>
										</svg>
										Previous
									</button>

									<div className="pagination__pages">
										{Array.from({ length: Math.min(5, currentPagination.pages) }, (_, i) => {
											let pageNum
											if (currentPagination.pages <= 5) {
												pageNum = i + 1
											} else if (currentPagination.page <= 3) {
												pageNum = i + 1
											} else if (currentPagination.page >= currentPagination.pages - 2) {
												pageNum = currentPagination.pages - 4 + i
											} else {
												pageNum = currentPagination.page - 2 + i
											}
											return (
												<button
													key={pageNum}
													className={`pagination__page ${currentPagination.page === pageNum ? 'pagination__page--active' : ''}`}
													onClick={() => handlePageChange(pageNum)}
												>
													{pageNum}
												</button>
											)
										})}
									</div>

									<button
										className="pagination__btn"
										disabled={currentPagination.page === currentPagination.pages}
										onClick={() => handlePageChange(currentPagination.page + 1)}
									>
										Next
										<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
											<polyline points="9 18 15 12 9 6"/>
										</svg>
									</button>
								</div>
							)}
						</>
					)}
				</div>
			</div>

			{/* Task Actions Modal */}
			<TaskActionsModal
				isOpen={modalState.isOpen}
				onClose={closeModal}
				task={modalState.task}
				action={modalState.action}
				userRole={userRoleSlug}
			/>
		</div>
	)
}

/**
 * Workflow Path Component
 * Displays workflow steps inline with color coding:
 * - Completed steps: green
 * - Current step: highlighted (blue background)
 * - Pending steps: muted (gray)
 */
const WorkflowPath = ({ task }) => {
	const { workflow_info } = task
	const totalSteps = workflow_info?.total_steps || 1
	const currentStepIndex = workflow_info?.current_step_index || 0
	const isCompleted = task.workflow_status === 'approved'

	// Parse workflow name to get individual step names
	// Expected format: "Compliance Manager → Risk Manager → Executive"
	const workflowSteps = task.workflow_name
		? task.workflow_name.split('→').map(s => s.trim())
		: []

	// If we don't have workflow steps from name, don't render
	if (workflowSteps.length === 0) return null

	return (
		<div className="workflow-path">
			<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
				<polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
			</svg>
			<div className="workflow-path__steps">
				{workflowSteps.map((stepName, idx) => {
					let status = 'pending'
					if (isCompleted || idx < currentStepIndex) {
						status = 'completed'
					} else if (idx === currentStepIndex) {
						status = 'current'
					}

					return (
						<span key={idx} className="workflow-path__step-wrapper">
							<span className={`workflow-path__step workflow-path__step--${status}`}>
								{stepName}
							</span>
							{idx < workflowSteps.length - 1 && (
								<span className="workflow-path__arrow">→</span>
							)}
						</span>
					)
				})}
			</div>
			<span className="workflow-path__progress">
				Step {currentStepIndex + 1}/{totalSteps}
			</span>
		</div>
	)
}

/**
 * Task Card Component
 * Compact design - entire card is clickable to open task details
 * Action indicator shows in header bar with small View button
 */
const TaskCard = ({ task, user, userRoleSlug, isApprovalView, onAction }) => {
	const statusColor = taskService.getStatusColor(task.workflow_status)
	const statusText = taskService.getStatusText(task.workflow_status)
	const isLocked = taskService.isLocked(task)

	// Determine available actions based on task status and user
	const canStart = taskService.canStart(task, user)
	const canSubmit = taskService.canSubmit(task, user)
	const canAccept = taskService.canAccept(task, userRoleSlug)
	const canComplete = taskService.canComplete(task, user)

	// Get action indicator text
	const getActionIndicator = () => {
		if (isLocked) return null
		if (isApprovalView && canAccept) {
			return { text: 'Needs Your Review', type: 'urgent' }
		}
		if (canStart) {
			return { text: 'Ready to Start', type: 'action' }
		}
		if (canSubmit) {
			return { text: 'Ready to Submit', type: 'submit' }
		}
		if (task.workflow_status === 'pending_review') {
			return { text: 'Awaiting Review', type: 'waiting' }
		}
		if (task.workflow_status === 'rejected') {
			return { text: 'Needs Revision', type: 'rejected' }
		}
		return null
	}

	const actionIndicator = getActionIndicator()

	// Handle card click - opens task details
	const handleCardClick = (e) => {
		// Prevent click if clicking on the view button (to avoid double action)
		if (e.target.closest('.task-card__view-btn')) return
		onAction('view', task)
	}

	return (
		<div
			className={`task-card task-card--compact ${isLocked ? 'task-card--locked' : ''} ${actionIndicator ? 'task-card--actionable' : ''}`}
			onClick={handleCardClick}
			role="button"
			tabIndex={0}
			onKeyDown={(e) => {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault()
					onAction('view', task)
				}
			}}
		>
			{/* Action Indicator Strip with View Button */}
			{actionIndicator && (
				<div className={`task-card__indicator task-card__indicator--${actionIndicator.type}`}>
					<div className="task-card__indicator-content">
						{actionIndicator.type === 'urgent' && (
							<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
								<circle cx="12" cy="12" r="10"/>
								<polyline points="12 6 12 12 16 14"/>
							</svg>
						)}
						{actionIndicator.type === 'action' && (
							<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
								<polygon points="5 3 19 12 5 21 5 3"/>
							</svg>
						)}
						{actionIndicator.type === 'submit' && (
							<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
								<line x1="22" y1="2" x2="11" y2="13"/>
								<polygon points="22 2 15 22 11 13 2 9 22 2"/>
							</svg>
						)}
						{actionIndicator.type === 'waiting' && (
							<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
								<circle cx="12" cy="12" r="10"/>
								<polyline points="12 6 12 12 16 14"/>
							</svg>
						)}
						{actionIndicator.type === 'rejected' && (
							<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
								<path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
								<line x1="12" y1="9" x2="12" y2="13"/>
								<line x1="12" y1="17" x2="12.01" y2="17"/>
							</svg>
						)}
						<span>{actionIndicator.text}</span>
					</div>
					<button
						className="task-card__view-btn"
						onClick={(e) => {
							e.stopPropagation()
							onAction('view', task)
						}}
					>
						<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
							<path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
							<circle cx="12" cy="12" r="3"/>
						</svg>
						View
					</button>
				</div>
			)}

			{/* No action indicator - show status bar with view button */}
			{!actionIndicator && (
				<div className="task-card__indicator task-card__indicator--default">
					<div className="task-card__indicator-content">
						<span className={`task-badge task-badge--${statusColor}`}>{statusText}</span>
						{isLocked && (
							<span className="task-card__locked-badge">
								<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
									<rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
									<path d="M7 11V7a5 5 0 0 1 10 0v4"/>
								</svg>
								Locked
							</span>
						)}
					</div>
					<button
						className="task-card__view-btn"
						onClick={(e) => {
							e.stopPropagation()
							onAction('view', task)
						}}
					>
						<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
							<path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
							<circle cx="12" cy="12" r="3"/>
						</svg>
						View
					</button>
				</div>
			)}

			<div className="task-card__main">
				<div className="task-card__header">
					<div className="task-card__title-section">
						<h3 className="task-card__title">{task.title}</h3>
						{actionIndicator && (
							<span className={`task-badge task-badge--${statusColor}`}>{statusText}</span>
						)}
					</div>
				</div>

				{task.description && (
					<p className="task-card__description">{task.description}</p>
				)}

				<div className="task-card__meta">
					<div className="task-meta-item">
						<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
							<path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
						</svg>
						<span>{task.project_name || 'N/A'}</span>
					</div>

					{task.due_date && (
						<div className="task-meta-item">
							<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
								<rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
								<line x1="16" y1="2" x2="16" y2="6"/>
								<line x1="8" y1="2" x2="8" y2="6"/>
								<line x1="3" y1="10" x2="21" y2="10"/>
							</svg>
							<span>Due: {new Date(task.due_date).toLocaleDateString()}</span>
						</div>
					)}
				</div>

				{/* Workflow Path with highlighted current step */}
				{task.workflow_info && task.workflow_info.total_steps > 0 && (
					<WorkflowPath task={task} />
				)}
			</div>
		</div>
	)
}

export default MyTasks
